import useSWRInfinite from 'swr/infinite';
import { mutate as globalMutate } from 'swr';
import axios from 'axios';
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';

export interface Estimate {
  id: string;
  number: string;
  customer_id: string;
  issue_date: string;
  expiry_date?: string;
  status: string;
  sub_total: number;
  tax_total: number;
  total_amount: number;
  terms?: string;
  notes?: string;
  converted_invoice_id?: string | null;
  display_name?: string;
  first_name?: string;
  last_name?: string;
  company_name?: string;
  billing_addr_line1?: string;
  billing_addr_city?: string;
  billing_addr_state?: string;
  billing_addr_zip?: string;
}

interface EstimatesResponse {
  estimates: Estimate[];
  nextCursor: string | null;
}

const PAGE_SIZE = 300;
const SEARCH_DEBOUNCE_MS = 300;

export const useEstimatesInfinite = (
  searchTerm?: string,
  statusFilter?: string,
  customerId?: string
) => {
  const { token, currentBusiness, isCookieMode, API_BASE_URL } = useAuth();
  const [debouncedSearchTerm, setDebouncedSearchTerm] = useState(searchTerm || '');
  const [allEstimates, setAllEstimates] = useState<Estimate[]>([]);

  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearchTerm(searchTerm || '');
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [searchTerm]);

  const getKey = (pageIndex: number, previousPageData: EstimatesResponse | null) => {
    if (previousPageData && !previousPageData.nextCursor) return null;

    const params = new URLSearchParams();
    if (debouncedSearchTerm) params.append('search', debouncedSearchTerm);
    if (statusFilter && statusFilter !== 'all') params.append('status', statusFilter);
    if (customerId) params.append('customer_id', customerId);
    params.append('limit', PAGE_SIZE.toString());

    if (pageIndex > 0 && previousPageData?.nextCursor) {
      params.append('after', previousPageData.nextCursor);
    }

    return ['/estimates', currentBusiness?.id, params.toString()];
  };

  const fetcher = async ([url, _businessId, params]: [string, string, string]) => {
    if (!currentBusiness) {
      throw new Error('Business context required');
    }

    if (!isCookieMode && !token) {
      throw new Error('Authentication required');
    }

    const headers: any = {};
    if (!isCookieMode && token) {
      headers.Authorization = `Bearer ${token}`;
    }

    const response = await axios.get(`${API_BASE_URL}${url}?${params}`, {
      headers,
      withCredentials: isCookieMode,
    });

    return response.data;
  };

  const {
    data,
    error,
    isLoading,
    isValidating,
    size,
    setSize,
    mutate,
  } = useSWRInfinite<EstimatesResponse>(getKey, fetcher, {
    revalidateOnFocus: false,
    revalidateOnReconnect: false,
    dedupingInterval: 0,
  });

  useEffect(() => {
    if (currentBusiness?.id) {
      setAllEstimates([]);
      setSize(1);
      globalMutate(() => true, undefined, { revalidate: false });
      mutate(undefined, { revalidate: true });
    }
  }, [currentBusiness?.id, mutate, setSize]);

  useEffect(() => {
    if (data) {
      const allFetched = data.flatMap((page) => page.estimates);
      setAllEstimates(allFetched);
    }
  }, [data]);

  const filteredEstimates = useCallback(() => {
    if (!searchTerm || !allEstimates.length) return allEstimates;

    const q = searchTerm.toLowerCase();
    return allEstimates.filter((estimate) => {
      if (estimate.number.toLowerCase().includes(q)) return true;
      const customerName = (
        estimate.display_name ||
        `${estimate.first_name || ''} ${estimate.last_name || ''}`.trim() ||
        estimate.company_name ||
        ''
      ).toLowerCase();
      if (customerName.includes(q)) return true;

      const address = `${estimate.billing_addr_line1 || ''} ${estimate.billing_addr_city || ''} ${estimate.billing_addr_state || ''} ${estimate.billing_addr_zip || ''}`.toLowerCase();
      return address.includes(q);
    });
  }, [allEstimates, searchTerm]);

  const estimates = filteredEstimates();
  const isLoadingMore = isLoading || (size > 0 && data && typeof data[size - 1] === 'undefined');
  const isEmpty = data?.[0]?.estimates?.length === 0;
  const isReachingEnd = isEmpty || (data && data[data.length - 1]?.nextCursor === null);
  const isRefreshing = isValidating && data && data.length === size;

  const loadMore = () => {
    if (!isLoadingMore && !isReachingEnd) {
      setSize(size + 1);
    }
  };

  return {
    estimates,
    error,
    isLoading,
    isLoadingMore,
    isReachingEnd,
    isRefreshing,
    loadMore,
    mutate,
    isEmpty,
  };
};
